import { Link } from "@inertiajs/react"
import { MessageCircle, Search } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import type { ConversationListRow } from "@/types/messaging"

/**
 * OKS-37 — Brand-side conversation list (left pane).
 *
 * Search is client-side-only (debounced 200ms by virtue of the
 * controlled `<Input>` re-render rate; no async). Selected row is
 * highlighted by `aria-current="page"` + accent fill.
 */
interface ConversationListProps {
  conversations: ConversationListRow[]
  currentMembershipId: number | null
  onPickCreator: () => void
  className?: string
}

export function ConversationList({
  conversations,
  currentMembershipId,
  onPickCreator,
  className,
}: ConversationListProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === "") return conversations
    return conversations.filter((c) =>
      c.creatorDisplayName.toLowerCase().includes(q),
    )
  }, [conversations, query])

  const totalUnread = conversations.reduce(
    (acc, c) => acc + (c.unread ? 1 : 0),
    0,
  )

  return (
    <aside
      className={cn(
        "border-border bg-background flex h-full w-full flex-col border-r",
        "lg:w-80 lg:shrink-0",
        className,
      )}
      data-testid="messaging-conversation-list"
      aria-label={t("messaging.brand.page_title")}
    >
      <div className="border-border flex items-center justify-between gap-2 border-b p-3">
        <h2 className="text-sm font-semibold">
          {t("messaging.brand.page_title")}
        </h2>
        {totalUnread > 0 && (
          <Badge
            variant="secondary"
            data-testid="messaging-list-unread-total"
            aria-label={t("messaging.brand.list.unread_aria", {
              count: totalUnread,
            })}
          >
            {totalUnread}
          </Badge>
        )}
      </div>
      <div className="border-border border-b p-2">
        <div className="relative">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("messaging.brand.list.search_placeholder")}
            className="pl-8"
            data-testid="messaging-list-search"
            aria-label={t("messaging.brand.list.search_placeholder")}
          />
        </div>
      </div>

      {conversations.length === 0 ? (
        <ConversationListEmptyState onPickCreator={onPickCreator} />
      ) : filtered.length === 0 ? (
        <p
          className="text-muted-foreground p-4 text-sm"
          data-testid="messaging-list-no-results"
        >
          {t("messaging.brand.list.no_results", { query })}
        </p>
      ) : (
        <ul
          role="list"
          className="flex-1 overflow-y-auto"
          data-testid="messaging-list-rows"
        >
          {filtered.map((conv) => (
            <ConversationListRowItem
              key={conv.id}
              row={conv}
              isSelected={currentMembershipId === conv.crewMembershipId}
            />
          ))}
        </ul>
      )}
    </aside>
  )
}

interface ConversationListRowItemProps {
  row: ConversationListRow
  isSelected: boolean
}

function ConversationListRowItem({
  row,
  isSelected,
}: ConversationListRowItemProps) {
  const { t } = useTranslation()
  const initials = useInitials()
  return (
    <li role="listitem" className="border-border border-b last:border-b-0">
      <Link
        href={row.url}
        only={[
          "conversations",
          "messages",
          "currentMembershipId",
          "currentConversation",
          "currentMembership",
          "canReply",
          "unread_messages_count",
        ]}
        preserveScroll
        preserveState
        aria-current={isSelected ? "page" : undefined}
        data-testid={`messaging-list-row-${row.crewMembershipId}`}
        className={cn(
          "hover:bg-accent flex items-start gap-3 p-3",
          "min-h-[64px]", // ≥44px touch target plus comfort
          isSelected && "bg-accent",
        )}
      >
        <Avatar className="size-10 shrink-0">
          <AvatarFallback>{initials(row.creatorDisplayName)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-medium">
              {row.creatorDisplayName}
            </p>
            {row.lastMessageAt && (
              <time
                dateTime={row.lastMessageAt}
                className="text-muted-foreground shrink-0 text-xs"
              >
                {formatRelative(
                  row.lastMessageAt,
                  t("messaging.list.relative.now"),
                )}
              </time>
            )}
          </div>
          <p
            className="text-muted-foreground mt-0.5 line-clamp-1 text-xs"
            data-testid="messaging-list-row-preview"
          >
            {row.lastMessagePreview ?? ""}
          </p>
        </div>
        {row.unread && (
          <span
            className="bg-primary mt-2 size-2 shrink-0 rounded-full"
            aria-label={t("messaging.brand.list.new_dot_aria")}
            data-testid={`messaging-list-row-unread-${row.crewMembershipId}`}
          />
        )}
      </Link>
    </li>
  )
}

function ConversationListEmptyState({
  onPickCreator,
}: {
  onPickCreator: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center"
      data-testid="messaging-list-empty-state"
    >
      <MessageCircle
        className="text-muted-foreground size-10"
        aria-hidden="true"
      />
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {t("messaging.brand.list.empty_title")}
        </p>
        <p className="text-muted-foreground text-xs">
          {t("messaging.brand.list.empty_description")}
        </p>
      </div>
      <button
        type="button"
        onClick={onPickCreator}
        className="text-primary text-xs font-medium hover:underline"
        data-testid="messaging-list-empty-pick-cta"
      >
        {t("messaging.brand.list.empty_cta")}
      </button>
    </div>
  )
}

export function ConversationListSkeleton() {
  return (
    <div
      className="flex h-full w-full flex-col gap-2 p-3"
      data-testid="messaging-list-skeleton"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2 py-1">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Compact relative-time label ("now", "2m", "3h", "5d", "Apr 3").
 * The `nowLabel` is locale-aware (passed in by the caller via `t()`);
 * numeric units stay i18n-neutral. `month: "short"` + the user's
 * runtime locale handle the "Apr 3" branch.
 */
function formatRelative(iso: string, nowLabel: string): string {
  const then = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - then.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return nowLabel
  if (diffMin < 60) return `${diffMin}m`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d`
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}
